<script setup>
import { ref, computed } from 'vue';
import { useMainStore } from '../store/index';
import { useConfiguratorApi } from '../composables/configurator';
import SpecificationModal from './SpecificationModal';
import InformationModal from './InformationModal';
import PriceSuffix from './PriceSuffix';
import PriceTooltip from './PriceTooltip.vue';
import Tooltip from './Tooltip.vue';
import MonthlyPriceTooltip from './MonthlyPriceTooltip';
import InformationIcon from './InformationIcon';
import ServiceSavingsProgressBar from './ServiceSavingsProgressBar.vue';
import lightning_bolt from '@/assets/lightning_bolt.svg';
import { useRouter, useRoute } from 'vue-router';

/* eslint-disable vue/no-deprecated-slot-attribute */ // Slot attribute used by SDDS
/* eslint-disable vue/no-v-html */
defineEmits(['click']);
const { totalFormattedPrice, totalPrice, repairMaintenanceBasePrice, currentServiceSavingsFormatted, maxServiceSavingsFormatted, serviceSavingsProgressPercent } = useConfiguratorApi();
const store = useMainStore();
const router = useRouter();
const route = useRoute();

const props = defineProps({
  images: {
    type: Object,
    default: () => {
      return {};
    },
  },
  price: {
    type: String,
    required: true,
  },
  priceSuffix: {
    type: String,
    required: true,
  },
  isBev: {
    type: Boolean,
    default: false,
  },
});

store.loadSpecification();

const specSearchText = ref('');
const specModalId = 'heroSection-specModal';
const heroImageBlobModalId = 'heroSection-heroImageBlobModal';
const operationalLeaseModalId = 'heroSection-operationalLeaseModal';
const paymentTypes = store.options.payment ? store.options.payment.find(o => o.id === 'paymentType').options : [];

const changePaymentType = (paymentType) => {
  store.selectedPaymentType = paymentType;
  store.selected.payment.paymentType = paymentTypes.find(p => p.value === paymentType);
};

const handleSearchInput = (input) => {
  specSearchText.value = input;
};

const handleSpecModalClick = () => {
  specSearchText.value = '';
};

const yearEndOfferPrice = computed(() => {
  if (store.showYearEndOfferBanner) {
    const calculatedPrice = (store.selectedPaymentType === 'leasing' ? totalPrice.value.price : totalPrice.value.monthlyPrice) - repairMaintenanceBasePrice.value + 1;
    return calculatedPrice.toLocaleString('en').replace(/,/g," ");
  } else return totalFormattedPrice.value.price;
});

const leasingYears = computed(() => {
  const contractLengths = store.options?.leasing?.find(x => x.id === 'contractLength')?.options;
  const min = contractLengths[0].value.split(' ')[0];
  const max = contractLengths[contractLengths.length-1].value.split(' ')[0];
  return [min/12, max/12];
});

const offerValidDate = computed(() => {
  if (!store.misc?.offerValidUntilDate) return ['N/A'];
  const date = new Date(store.misc.offerValidUntilDate);
  return [`${String(date.getDate()).padStart(2, '0')}.${(String(date.getMonth()+1)).padStart(2, '0')}.${String(date.getFullYear()).substring(2)}`];
});

const checkIfBev = computed(() => {
  return store.misc?.template?.trim()?.toUpperCase().endsWith('_BEV');
});

const heroImageBlobs = computed(() => {
  return store.misc?.heroImageBlobs || {};
});

const showHeroImageBlobs = computed(() => {
  return Object.keys(heroImageBlobs.value).length;
});

const goToActive = () => {
  router.push({ name: 'PublicActiveOffersView', params: { market: store.market }, query: { ref: route.query.ref } });
};

const heroImage = computed(() => {
  return props.images[store.activeMarket?.defaultHeroImageType];
});

// Need to hide buyBack value and operational-leasing-text temporarily for time being
// because Germany was upset with the wrong information being shown in the tooltip and operational-leasing-text.
// const showFinancingTextTemporarily = false;
const showFinancingText = computed(() => {
  return store.activeMarket?.showHeroSectionFinancingText;
});
const showConfigureChevron = computed(() => {
  return store.activeMarket?.showConfigureChevron;
});
const defaultFinancingTextByPaymentType = {
  leasing: {
    textKey: 'operationalPublicLeaseHeroText',
    linkTextKey: 'operationalLeaseSeeMoreLinkText',
    modalTitleKey: 'operationalLeaseModalTitle',
    modalTextKey: 'operationalLeaseModalText',
    analyticsLabel: 'view terms and conditions - leasing',
  },
  cash: {
    textKey: 'operationalCashHeroText',
    linkTextKey: 'operationalCashSeeMoreLinkText',
    modalTitleKey: 'operationalCashModalTitle',
    modalTextKey: 'operationalCashModalText',
    analyticsLabel: 'view terms and conditions - cash',
  },
};
const financingText = computed(() => {
  const paymentType = store.selectedPaymentType;
  const defaults = defaultFinancingTextByPaymentType[paymentType];
  const configuredValue = store.misc.alternativeFinancingText?.[paymentType];

  if (!defaults) return null;

  const [ textKey, linkTextKey, modalTitleKey, modalTextKey ] = configuredValue?.split(',') ?? [];

  return {
    ...defaults,
    textKey: textKey || defaults.textKey,
    linkTextKey: linkTextKey || defaults.linkTextKey,
    modalTitleKey: modalTitleKey || defaults.modalTitleKey,
    modalTextKey: modalTextKey || defaults.modalTextKey,
  };
});
</script>

<template>
  <div class="hero">
    <div class="hero__content">
      <div class="hero__image">
        <span
          class="go-back-available-offers"
        >
          <svg
            class="el-margin-right-8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14.046 5.685a1 1 0 0 0-1.414-1.415l-9.9 9.9a2.6 2.6 0 0 0 0 3.678l9.9 9.9a1 1 0 1 0 1.415-1.415L4.722 17.01h24.306a1 1 0 0 0 0-2H4.722l9.325-9.324Z"
              fill="currentColor"
            />
          </svg>
          <a
            data-analytics="{'eventComponent':'hero','eventType':'link','eventLabel':'go back to available offers'}"
            data-dd-action-name="go back to available offers"
            @click="goToActive"
          >{{ $i18n('goBackToAvailableOffers') }}
          </a>
        </span>
        <img
          :src="heroImage"
          alt="Truck"
        >
        <!-- <div class="image-description">
          <a
            :id="specModalId"
            data-analytics="{'eventComponent':'hero','eventType':'modal','eventLabel':'view specification'}"
            href="#"
            @click.prevent="handleSpecModalClick()"
          >{{ $i18n('viewSpecificationLinkText') }}</a> <font-awesome-icon icon="magnifying-glass" />
        </div> -->
        <div
          v-if="showHeroImageBlobs"
          class="hero-image-blobs-container"
        >
          <div
            v-for="(heroImageBlobColors, heroImageBlobTranslationKey) in heroImageBlobs"
            :key="heroImageBlobTranslationKey"
            class="hero-image-blob"
            :style="(() => {
              const returnStyleObjct = {};
              if (heroImageBlobColors?.split(',')[0]) returnStyleObjct.backgroundColor = heroImageBlobColors.split(',')[0];
              if (heroImageBlobColors?.split(',')[1]) returnStyleObjct.color = heroImageBlobColors.split(',')[1];
              return returnStyleObjct;
            })()"
          >
            <span class="hero-image-blob-text">{{ $i18n('eMobBlobUpToText') }} <img :src="lightning_bolt"></span>
            <span class="hero-image-blob-text-value">{{ $i18n(heroImageBlobTranslationKey) }}</span>
            <a
              :id="heroImageBlobModalId"
              data-analytics="{'eventComponent':'hero','eventType':'modal','eventLabel':'view blob info'}"
              href="#"
              style="color: inherit;"
              @click.prevent="handleSpecModalClick()"
            >{{ $i18n('moreInformationText') }}</a> <InformationIcon
              :id="heroImageBlobModalId"
              view-box="-6 -6 48 48"
              style="color: inherit;"
            />
          </div>
        </div>
      </div>
      <div class="hero__value-prop">
        <div :class="{ 'hero__value-prop-content-div-responsive': paymentTypes.length > 1 || store.selectedPaymentType === 'cash' }">
          <h1
            v-if="store.misc.heroSectionHeader"
            v-html="$i18n(store.misc.heroSectionHeader)"
          />
          <h1
            v-else
            v-html="store.selectedPaymentType === 'cash' ? $i18n('heroHeaderCash') : $i18n('heroHeader')"
          />
          <p
            v-if="store.misc.heroSectionBody"
            class="sdds-paragraph-01"
          >
            {{ $i18n(store.misc.heroSectionBody) }}
          </p>
          <p
            v-else
            class="sdds-paragraph-01"
          >
            {{ store.selectedPaymentType === 'cash' ? $i18n('heroBodyCash') : $i18n('heroBody') }}
          </p>
          <ul
            v-if="store.misc.heroSectionBullets?.length"
            class="sdds-body-01 hero-bullet-container-min-height"
          >
            <li
              v-for="heroSectionBullet in store.misc.heroSectionBullets"
              :key="heroSectionBullet"
            >
              {{ $i18n(heroSectionBullet) }}
            </li>
          </ul>
          <ul
            v-else
            class="sdds-body-01 hero-bullet-container-fixed-height"
          >
            <template v-if="store.selectedPaymentType === 'leasing'">
              <li>{{ $i18n('heroBulletOne', leasingYears) }}</li>
            </template>
            <li>{{ $i18n('heroBulletTwo') }}</li>
            <li>{{ $i18n('heroBulletFour') }}</li>
            <template v-if="store.market === 'pl'">
              <li>{{ $i18n('heroBulletFive') }}</li>
            </template>
          </ul>
          <template v-if="store.activeMarket.showPrice && paymentTypes.length">
            <div
              class="hero-section-payment-container"
              :class="{
                'hero-section-payment-container-only-leasing': paymentTypes.length === 1 && store.selectedPaymentType === 'leasing' && !(store.serviceSavings.length),
                'hero-section-payment-container-height-115': !(store.serviceSavings.length),
                'hero-section-payment-container-with-savings': store.serviceSavings.length,
              }"
            >
              <div class="hero-section-paymentType-div">
                <div
                  v-for="paymentType in paymentTypes"
                  :key="paymentType.id"
                  class="hero-section-paymentType-option-div"
                >
                  <a
                    :data-analytics="`{'eventComponent':'sidebar','eventType':'button','eventLabel':'change payment type - ${paymentType.value}'}`"
                    class="sdds-navigation-tabs--tab"
                    :class="{
                      'sdds-navigation-tabs--tab__active': paymentType.value === store.selectedPaymentType,
                      'sdds-navigation-tabs--tab__unique': paymentTypes.length === 1,
                      'sdds-navigation-tabs--tab__inactive': paymentType.value !== store.selectedPaymentType,
                    }"
                    :data-dd-action-name="`change payment type - ${paymentType.value}`"
                    @click="changePaymentType(paymentType.value)"
                  >{{ $i18n(paymentType.value) }}</a>
                </div>
              </div>
              <div
                style="
                  display: flex;
                  flex-direction: column;
                "
              >
                <div class="hero-section-sdds-divider-light sdds-divider-light sdds-u-mt0" />
                <div
                  class="hero-section-payment-info"
                  :class="{ 'hero-section-payment-info-leasing-padding': paymentTypes.length > 1 && store.selectedPaymentType === 'leasing' }"
                >
                  <template v-if="store.selectedPaymentType === 'leasing'">
                    <span class="sdds-u-m-auto">
                      <template v-if="store.showYearEndOfferBanner">
                        <span class="year-end-offer-price hero-price-main">{{ yearEndOfferPrice }}</span>&nbsp;<span><PriceSuffix :price-suffix="totalFormattedPrice.priceSuffix" /></span>
                        <template v-if="store.activeMarket.showPriceTooltip">
                          <span class="el-margin-left-8">
                            <PriceTooltip
                              placement="bottom"
                              selector-id="price-tooltip-hero-section"
                            />
                          </span>
                        </template>
                      </template>
                      <template v-else-if="checkIfBev">
                        <span class="show-discounted-price"><b>{{ totalFormattedPrice.price }}</b>&nbsp;<span class="discounted-price-suffix">{{ $i18n(totalFormattedPrice.priceSuffix) }}</span></span>
                        <div class="show-non-discounted-price"><s>{{ totalFormattedPrice.priceAndDownPaymentPricePerMonthWithoutService }}&nbsp;{{ $i18n(totalFormattedPrice.priceSuffix) }}</s>
                          <template v-if="store.activeMarket.showPriceTooltip">
                            <span class="el-margin-left-8">
                              <PriceTooltip
                                placement="bottom"
                                selector-id="price-tooltip-hero-section"
                              />
                            </span>
                          </template>
                        </div>
                      </template>
                      <template v-else>
                        <span class="hero-price-main">{{ totalFormattedPrice.price }}</span>&nbsp;<span><PriceSuffix :price-suffix="totalFormattedPrice.priceSuffix" /></span>
                        <template v-if="store.activeMarket.showPriceTooltip">
                          <span class="el-margin-left-8">
                            <PriceTooltip
                              placement="bottom"
                              selector-id="price-tooltip-hero-section"
                            />
                          </span>
                        </template>
                      </template>
                    </span>
                  </template>
                  <template v-if="store.selectedPaymentType === 'cash'">
                    <div class="price-wrapper">
                      <div class="price-content">
                        <span class="sdds-u-block sdds-detail-02">{{ $i18n('purchasePrice') }}</span>
                        <div>
                          <span class="payment-amount">{{ totalFormattedPrice.price }}</span>&nbsp;<span><PriceSuffix :price-suffix="totalFormattedPrice.priceSuffix" /></span>
                        </div>
                      </div>
                    </div>
                    <span class="sdds-divider-dark-vertical" />
                    <div class="price-wrapper">
                      <div class="price-content">
                        <span class="sdds-u-block sdds-detail-02">{{ $i18n('monthlyPrice') }}</span>
                        <div>
                          <template v-if="store.showYearEndOfferBanner">
                            <span class="payment-amount year-end-offer-price">{{ yearEndOfferPrice }}</span>&nbsp;<span><PriceSuffix :price-suffix="totalFormattedPrice.priceSuffixMonthly" /></span>
                            <template v-if="store.activeMarket.showPriceTooltip">
                              <span class="el-margin-left-8"><MonthlyPriceTooltip selector-id="monthly-price-tooltip-hero-section" /></span>
                            </template>
                          </template>
                          <template v-else>
                            <span class="payment-amount">{{ totalFormattedPrice.monthlyPrice }}</span>&nbsp;<span><PriceSuffix :price-suffix="totalFormattedPrice.priceSuffixMonthly" /></span>
                            <template v-if="store.activeMarket.showPriceTooltip">
                              <span class="el-margin-left-8"><MonthlyPriceTooltip selector-id="monthly-price-tooltip-hero-section" /></span>
                            </template>
                          </template>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <template v-if="store.serviceSavings.length">
                  <div class="service-savings-price-container">
                    <div class="service-savings-card__header">
                      <span>{{ $i18n('serviceSavings') }}</span>
                      <Tooltip
                        selector-id="service-savings-price-tooltip-hero-section"
                        placement="bottom"
                        :text="$i18n('serviceSavingsInformation')"
                        :icon-style="{ color: '#0B1320', width: '3.2rem' }"
                      />
                    </div>
                    <div class="service-savings-card__body">
                      <ServiceSavingsProgressBar
                        class="service-savings-progress"
                        :percent="serviceSavingsProgressPercent"
                        :show-dot="false"
                      />
                      <span
                        class="service-savings-progress__values"
                        translate="no"
                      >
                        {{ currentServiceSavingsFormatted }} / {{ maxServiceSavingsFormatted }}&nbsp;<span class="service-savings-progress__suffix"><PriceSuffix :price-suffix="totalFormattedPrice.priceSuffixOneTime" /></span>
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
          <button
            type="button"
            class="sdds-btn sdds-btn-primary"
            data-analytics="{'eventComponent':'hero','eventType':'button','eventLabel':'show contact request modal'}"
            data-dd-action-name="show contact request modal"
            @click="$emit('click', 'proceed')"
          >
            {{ $i18n('continue') }}
          </button>
        </div>
        <template v-if="showFinancingText">
          <div class="operational-text">
            <p
              v-if="financingText?.textKey"
              class="foot-note sdds-detail-05"
            >
              {{ $i18n(financingText.textKey) }}
              <a
                v-if="financingText.linkTextKey"
                :id="operationalLeaseModalId"
                :data-analytics="`{'eventComponent':'hero','eventType':'modal','eventLabel':'${financingText.analyticsLabel}'}`"
                href="#"
                :data-dd-action-name="financingText.analyticsLabel"
                @click.prevent
              >{{ $i18n(financingText.linkTextKey) }}</a>
            </p>
          </div>
        </template>
      </div>
    </div>
    <template v-if="showConfigureChevron">
      <div class="hero__navigation">
        <button
          class="hero__navigation-button"
          data-analytics="{'eventComponent':'hero','eventType':'button','eventLabel':'fine tune this truck'}"
          data-dd-action-name="fine tune this truck"
          @click="$emit('click', 'configure')"
        >
          <div class="hero__navigation-button-icon">
            <svg
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M4.273 9.783a1 1 0 0 1 1.415 0l9.888 9.888a.6.6 0 0 0 .848 0l9.888-9.888a1 1 0 1 1 1.415 1.414l-9.889 9.889a2.6 2.6 0 0 1-3.677 0l-9.888-9.889a1 1 0 0 1 0-1.414Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <span>{{ $i18n('fineTuneText') }}</span>
        </button>
      </div>
    </template>
  </div>
  <SpecificationModal
    :id="specModalId"
    :search-text="specSearchText"
    @search="handleSearchInput"
  />
  <InformationModal
    v-if="financingText?.modalTextKey"
    :id="operationalLeaseModalId"
    :title="financingText.modalTitleKey"
    :information-text="financingText.modalTextKey"
  />
  <InformationModal
    v-if="showHeroImageBlobs"
    :id="heroImageBlobModalId"
    title="heroImageBevBlobModalTitle"
    information-text="heroImageBevBlobModalText"
  />
</template>

<style scoped>
  .hero {
    display: flex;
    flex-direction: column;
    box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
    background-color: var(--sdds-grey-100);
    position: relative;
  }

  .hero__content {
    display: flex;
    place-content: center;
    flex-wrap: wrap-reverse;
    flex-grow: 3;
  }
  .hero__image {
    position: relative;
    flex: 0.5 1 632px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: var(--sdds-spacing-layout-64);
  }
  .hero__image > img {
    object-fit: cover;
    max-width: 100%;
  }
  .hero__image > .image-description {
    margin-top: -30px;
  }

  .hero__value-prop {
    flex: 0.5 1 632px;
    display: grid;
    place-items: center;
    padding: var(--sdds-spacing-layout-16);
  }
  .hero__value-prop ul {
    list-style-type: none;
    padding-inline-start: 0px;
    margin-bottom: var(--sdds-spacing-layout-24);
  }
  .hero__value-prop li {
    margin-bottom: var(--sdds-spacing-layout-8);
  }
  .hero__value-prop li:before {
    content: '\2713';
    margin-right: var(--sdds-spacing-layout-16);
    font-weight: 600;
    color: v-bind('store.activeMarket?.highlightColor');
  }

  .hero__navigation {
    display: grid;
    place-items: center;
    margin-bottom: 1.2em;
    color: var(--sdds-grey-846);
  }
  .hero__navigation-button {
    background: none;
    border: none;
    cursor: pointer;
  }
  .hero__navigation-button svg {
    width: 3em;
    margin-bottom: -0.5em;
  }
  .hero__navigation-button-icon {
    -webkit-animation: pulse 5s infinite ease-in-out;
    -o-animation: pulse 5s infinite ease-in-out;
    -ms-animation: pulse 5s infinite ease-in-out; 
    -moz-animation: pulse 5s infinite ease-in-out; 
    animation: pulse 5s infinite ease-in-out;
  }

  @-webkit-keyframes pulse {
    0% { opacity: 0.3; }
    50% { opacity: 0.6; }
    100% { opacity: 0.3; }
  }

  @keyframes pulse {
    0% { opacity: 0.3; }
    50% { opacity: 0.6; }
    100% { opacity: 0.3; }
  }

  .foot-note {
    color: var(--sdds-grey-700);
    margin: var(--sdds-spacing-element-16) 0;
  }

  button svg {
    margin-right: var(--sdds-spacing-element-8);
  }
  .hero-section-payment-container {
    display: flex;
    flex-direction: column;
    width: 380px;
  }
  .hero-section-payment-container + .sdds-btn.sdds-btn-primary {
    margin-top: 16px;
  }
  .hero-section-payment-container-with-savings {
    width: min(100%, 450px);
    background-color: #e7e8ec;
    border: 1px solid #d6d9e0;
    border-radius: 4px;
    padding: 10px 14px 14px;
  }
  .hero-section-payment-container-with-savings .hero-section-paymentType-div {
    padding-top: 0;
  }
  .hero-section-payment-container-with-savings .hero-section-payment-info {
    padding: 12px 0 8px;
  }
  .hero-section-payment-container-height-115 {
    height: 115px;
  }
  .hero-section-paymentType-div {
    display: inline-flex;
    width: 100%;
    justify-content: space-evenly;
    padding-top: 4px;
  }
  .hero-section-paymentType-div .hero-section-paymentType-option-div {
    display: inline-flex;
    width: 50%;
  }
  .hero-section-paymentType-div .hero-section-paymentType-option-div a.sdds-navigation-tabs--tab {
    padding: 7px 0px;
    flex: auto;
    cursor: pointer;
    text-align: center;
  }
  .hero-section-paymentType-div .hero-section-paymentType-option-div a.sdds-navigation-tabs--tab::after {
    z-index: 2;
  }
  .hero-section-paymentType-div .hero-section-paymentType-option-div a.sdds-navigation-tabs--tab.sdds-navigation-tabs--tab__inactive {
    border: none;
  }
  .hero-section-payment-container .hero-section-payment-info {
    font-size: 3em;
    white-space: nowrap;
    display: flex;
    padding: 10px 0;
  }
  .hero-section-payment-container .hero-section-payment-info .payment-amount {
    font-size: 1em;
  }
  .hero-price-main {
    font-weight: 650;
  }
  .priceSuffix__container {
    font-size: 0.4em;
    line-height: 1em;
  }
  .hero-section-payment-container .hero-section-payment-info.hero-section-payment-info-leasing-padding {
    padding: 20px 0;
  }
  .sdds-divider-dark-vertical {
    height: 40px;
    width: 2px;
    display: inline-flex;
  }
  .price-wrapper {
    width: calc(50% - 1px);
    display: inline-flex;
  }
  .price-content {
    margin: 0 auto;
  }
  .operational-text {
    justify-self: flex-start;
  }

  .hero-section-paymentType-div .hero-section-paymentType-option-div a.sdds-navigation-tabs--tab__unique::after {
    background-color: transparent;
  }

  .hero-section-payment-container-only-leasing {
    width: 170px;
  }

  .hero-section-payment-container-only-leasing .hero-section-paymentType-div .hero-section-paymentType-option-div {
    width: 100%;
  }

  span.go-back-available-offers {
    position: absolute;
    top: var(--sdds-spacing-layout-16);
    left: var(--sdds-spacing-layout-32);
    cursor: pointer;
  }

  span.go-back-available-offers > svg {
    width: 5rem;
    vertical-align: bottom;
  }

  .hero-image-blobs-container {
    position: absolute;
    top: 5%;
    left: 60%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .hero-image-blob {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    color: #fff;
    font-size: 3rem;
    font-weight: bold;
    height: 180px;
    width: 180px;
    border-radius: 50%;
    padding-top: 6%;
  }

  .hero-image-blob-text {
    font-weight: normal;
    margin-bottom: 0.5rem;
    font-size: 5rem;
  }

  .hero-image-blob-text img {
    height: 24px;
    vertical-align: text-bottom;
  }

  .hero-image-blob-text-value {
    font-size: 14rem;
    line-height: 0.8;
  }

  .service-savings-price-container {
    color: var(--sdds-black);
    background-color: #9BD9C2;
    border-radius: 4px;
    padding: 14px 16px;
    text-align: left;
    margin-top: 8px;
  }
  .service-savings-card__header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 10px;
  }
  .service-savings-card__body {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .service-savings-progress {
    flex: 1;
    min-width: 0;
  }
  .service-savings-progress__values {
    font-size: 16px;
    white-space: nowrap;
    line-height: 1.1;
  }
  .service-savings-progress__suffix :deep(.priceSuffix__container) {
    font-size: 0.75em;
    line-height: 1;
  }

  @media only screen and (min-width: 1295px) {
    .hero-bullet-container-fixed-height {
      min-height: 152px;
      max-height: 152px;
      height: 152px;
    }
    .hero-bullet-container-min-height {
      min-height: 152px;
    }
    .operational-text {
      height: 64px;
    }
  }

  @media only screen and (max-width: 1294px) {
    .hero {
      height: 100%;
      padding-bottom: var(--sdds-spacing-layout-24);
    }
    span.go-back-available-offers {
      top: var(--sdds-spacing-layout-8);
    }
    .hero__image img {
      width: 60%;
    }
    .hero__navigation {
      display: none;
    }
    .hero-section-payment-container {
      display: none;
    }
  }

  @media only screen and (max-width: 720px) {
    .hero__image {
      padding-top: var(--sdds-spacing-layout-48);
    }
    .image-description {
      margin-top: -10px;
    }
    span.go-back-available-offers {
      left: var(--sdds-spacing-layout-16);
    }
  }
  @media only screen and (max-width: 530px) {
    .hero-section-payment-container .hero-section-payment-info .payment-amount {
      font-size: 0.8em;
    }
  }
  @media only screen and (max-width: 500px) {
    .hero__value-prop-content-div-responsive {
      padding-top: 2em;
    }
  }
  @media only screen and (max-width: 410px) {
    .hero-section-payment-container .hero-section-payment-info .payment-amount {
      font-size: 0.6em;
    }
    .hero-section-payment-container .hero-section-payment-info .sdds-detail-02 {
      font-size: calc(((14/ 4)) * 0.9rem);
    }
    .hero-section-payment-container .hero-section-payment-info .priceSuffix__container {
      font-size: 0.36em;
    }
  }
</style>
